import React, { useRef, useState, useCallback } from "react";
import { Alert } from "@mui/material";

const FileUploadDropzone = ({
  onFilesSelected,
  maxFiles = 1,
  maxSize = 5 * 1024 * 1024, // 5MB default
  accept = "image/*", // Default to images for avatars
  className = "",
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadProgress, setUploadProgress] = useState([]);
  const dropzoneRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleFiles = useCallback(
    (files) => {
      const validFiles = Array.from(files).filter((file) => {
        console.log(
          `File: ${file.name}, Type: ${file.type}, Size: ${file.size} bytes`
        ); // Debug log
        if (file.size > maxSize) {
          setError(
            `File ${file.name} is too large (${(
              file.size /
              1024 /
              1024
            ).toFixed(2)}MB, max ${maxSize / 1024 / 1024}MB)`
          );
          return false;
        }
        const acceptPattern = accept
          .split(",")
          .map((type) => type.trim().replace("*", ".*"))
          .join("|");
        if (
          !file.type ||
          !file.type.match(new RegExp(`^(${acceptPattern})$`, "i"))
        ) {
          setError(
            `File ${file.name} has invalid type (${
              file.type || "unknown"
            }); accepted: ${accept}`
          );
          return false;
        }
        return true;
      });

      if (validFiles.length > 0) {
        setError("");
        setUploadProgress(
          validFiles
            .slice(0, maxFiles)
            .map((file) => ({ fileName: file.name, progress: 0 }))
        );
        onFilesSelected(
          validFiles.slice(0, maxFiles),
          setUploadProgress,
          setError,
          setSuccess
        );
      } else if (files.length > 0) {
        setError(
          `No valid files selected. Check file types (${accept}) and size (max ${
            maxSize / 1024 / 1024
          }MB).`
        );
      }
    },
    [onFilesSelected, maxSize, accept, maxFiles]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  const handleChange = useCallback(
    (e) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
        e.target.value = ""; // Reset input for re-selection
      }
    },
    [handleFiles]
  );

  return (
    <div
      ref={dropzoneRef}
      className={`dropzone ${dragActive ? "drag-active" : ""} ${className}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() =>
        dropzoneRef.current?.querySelector('input[type="file"]')?.click()
      }
    >
      <input
        type="file"
        multiple={maxFiles > 1}
        accept={accept}
        onChange={handleChange}
        style={{ display: "none" }}
      />
      <div className="dropzone-content">
        <div className="icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.66675 13.3333L10.0001 10M10.0001 10L13.3334 13.3333M10.0001 10V17.5M16.6667 13.9524C17.6847 13.1117 18.3334 11.8399 18.3334 10.4167C18.3334 7.88536 16.2814 5.83333 13.7501 5.83333C13.568 5.83333 13.3976 5.73833 13.3052 5.58145C12.2185 3.73736 10.2121 2.5 7.91675 2.5C4.46497 2.5 1.66675 5.29822 1.66675 8.75C1.66675 10.4718 2.36295 12.0309 3.48921 13.1613"
              stroke="#FEFEFE"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p>
          <strong>Click to upload</strong> or drag and drop
        </p>
        <span className="file-types">SVG, PNG, JPG or GIF (2MB max)</span>
        {error && (
          <Alert severity="error" sx={{ mt: 4 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 4 }}>
            Profile picture updated successfully
          </Alert>
        )}
        {/* {uploadProgress.length > 0 && (
          <div className="progress-container">
            {uploadProgress.map(({ fileName, progress }, index) => (
              <div key={index} className="progress-item">
                <span>{fileName}</span>
                <progress value={progress} max="100">
                  {progress}%
                </progress>
                <span>{progress}%</span>
              </div>
            ))}
          </div>
        )} */}
      </div>
    </div>
  );
};

export default FileUploadDropzone;
